<script lang="ts">
	import { fade, fly } from 'svelte/transition';
	import { page } from '$app/state';
	import { browser } from '$app/env';
	import { onDestroy, onMount } from 'svelte';
	import JsonTree from '#lib/components/Toolbar/JsonTree.svelte';

	// --- state ---
	let open = $state(false);
	let activeTab = $state<'env' | 'logs' | 'page'>('env');
	let logLevel = $state('all');
	let logFile = $state('combined');
	let logs = $state<Array<Record<string, unknown>>>([]);
	let liveLog = $state(true);
	let logSearch = $state('');
	let logsLoading = $state(false);
	let autoScroll = $state(false);

	// --- drag ---
	const LS_KEY = 'dev-toolbar-pos';
	let pos = $state({ x: 0, y: 0 });
	let floatingEl = $state<HTMLElement | null>(null);
	let dragging = false;
	let dragOffset = { x: 0, y: 0 };
	let dragCurrent = { x: 0, y: 0 };

	// --- env ---
	let envPriv = $state(page.data.envPriv);

	let isBddLocal = $derived.by(() => {
		if (!browser || !envPriv?.DATABASE_URL) {
			return false;
		}
		return envPriv.DATABASE_URL.includes('localhost') || envPriv.DATABASE_URL.includes('127.0.0.1');
	});

	// --- log filtering ---
	let filteredLogs = $derived.by(() => {
		let result = logs;
		if (logLevel !== 'all') {
			result = result.filter((l) => l.level === logLevel);
		}
		if (logSearch.trim()) {
			const q = logSearch.toLowerCase();
			result = result.filter(
				(l) =>
					String(l.message ?? '')
						.toLowerCase()
						.includes(q) ||
					String(l.route ?? '')
						.toLowerCase()
						.includes(q)
			);
		}
		return result;
	});

	// --- SSE ---
	let eventSource: EventSource | null = null;

	function startSSE() {
		stopSSE();
		if (!browser) {
			return;
		}
		eventSource = new EventSource(`/dev/logs/stream?file=${logFile}`);
		eventSource.onmessage = (e) => {
			try {
				const raw = JSON.parse(e.data);
				const entry = typeof raw === 'string' ? JSON.parse(raw) : raw;
				logs = [entry, ...logs].slice(0, 500);
			} catch {}
		};
	}

	function stopSSE() {
		eventSource?.close();
		eventSource = null;
	}

	async function fetchLogs() {
		logsLoading = true;
		try {
			const res = await fetch(`/dev/logs?file=${logFile}&limit=200`);
			const data = await res.json();
			logs = data.logs ?? [];
		} catch {
			logs = [];
		} finally {
			logsLoading = false;
		}
	}

	$effect(() => {
		if (!browser || activeTab !== 'logs') {
			return;
		}
		fetchLogs().then(() => {
			if (liveLog) {
				startSSE();
			}
		});
		return () => stopSSE();
	});

	$effect(() => {
		if (!browser || activeTab !== 'logs') {
			return;
		}
		if (liveLog) {
			startSSE();
		} else {
			stopSSE();
		}
	});

	// --- drag logic ---
	function loadPos() {
		try {
			const saved = localStorage.getItem(LS_KEY);
			if (saved) {
				const p = JSON.parse(saved);
				return {
					x: Math.min(p.x, window.innerWidth - 100),
					y: Math.min(p.y, window.innerHeight - 40)
				};
			}
		} catch (_) {
			/* ignore */
		}
		return { x: window.innerWidth - 400, y: window.innerHeight - 48 };
	}

	function savePos(x: number, y: number) {
		try {
			localStorage.setItem(LS_KEY, JSON.stringify({ x, y }));
		} catch (_) {
			/* ignore */
		}
	}

	function applyPos(x: number, y: number) {
		if (floatingEl) {
			floatingEl.style.left = `${x}px`;
			floatingEl.style.top = `${y}px`;
		}
	}

	let didDrag = false;

	function startDrag(e: MouseEvent) {
		dragging = true;
		didDrag = false;
		dragOffset = { x: e.clientX - pos.x, y: e.clientY - pos.y };
		e.preventDefault();
		e.stopPropagation();
	}

	function startDragPanel(e: MouseEvent) {
		if ((e.target as HTMLElement).closest('button, input, select, a')) {
			return;
		}
		dragging = true;
		dragOffset = { x: e.clientX - pos.x, y: e.clientY - pos.y };
		e.preventDefault();
	}

	function onMouseMove(e: MouseEvent) {
		if (!dragging) {
			return;
		}
		const x = Math.max(0, Math.min(e.clientX - dragOffset.x, window.innerWidth - 100));
		const y = Math.max(0, Math.min(e.clientY - dragOffset.y, window.innerHeight - 40));
		applyPos(x, y);
		dragCurrent = { x, y };
		didDrag = true;
	}

	function onMouseUp() {
		if (dragging) {
			pos = { x: dragCurrent.x, y: dragCurrent.y };
			savePos(dragCurrent.x, dragCurrent.y);
		}
		dragging = false;
	}

	onMount(() => {
		pos = loadPos();
		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
	});

	onDestroy(() => {
		if (!browser) {
			return;
		}
		window.removeEventListener('mousemove', onMouseMove);
		window.removeEventListener('mouseup', onMouseUp);
		stopSSE();
	});

	function levelColor(level: string) {
		switch (level) {
			case 'error':
				return 'text-red-400';
			case 'warn':
				return 'text-yellow-400';
			case 'debug':
				return 'text-purple-400';
			default:
				return 'text-emerald-400';
		}
	}

	function levelBg(level: string) {
		switch (level) {
			case 'error':
				return 'bg-red-500/10';
			case 'warn':
				return 'bg-yellow-500/10';
			case 'debug':
				return 'bg-purple-500/10';
			default:
				return '';
		}
	}

	function formatTime(ts: string | null | undefined) {
		if (!ts) {
			return '';
		}
		try {
			return new Date(ts).toLocaleTimeString('fr-FR', {
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit'
			});
		} catch {
			return ts;
		}
	}

	function copyEnvValue(value: unknown) {
		if (browser) {
			navigator.clipboard.writeText(String(value));
		}
	}
</script>

<!-- Floating pill button when closed -->
{#if !open}
	<button
		bind:this={floatingEl}
		class="fixed z-[9999] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg border cursor-grab select-none transition-colors
			{isBddLocal
			? 'bg-yellow-500 border-yellow-600 text-yellow-950 hover:bg-yellow-400'
			: 'bg-red-500 border-red-600 text-white hover:bg-red-400'}"
		style="left: {pos.x}px; top: {pos.y}px;"
		onmousedown={startDrag}
		onclick={() => {
			if (!didDrag) {
				open = true;
			}
		}}
		transition:fade={{ duration: 150 }}
	>
		<span class="w-1.5 h-1.5 rounded-full {isBddLocal ? 'bg-yellow-900' : 'bg-white'} animate-pulse"
		></span>
		{isBddLocal ? 'LOCAL' : 'PROD'}
		<span class="opacity-50">DEV</span>
	</button>
{/if}

<!-- Main panel -->
{#if open}
	<div
		bind:this={floatingEl}
		class="fixed z-[9999] w-[480px] rounded-xl shadow-2xl border border-white/10 bg-gray-950/95 backdrop-blur-sm flex flex-col overflow-hidden"
		style="left: {pos.x}px; top: {pos.y}px; max-height: 520px;"
		transition:fly={{ y: 12, duration: 180 }}
	>
		<!-- Header / drag handle -->
		<div
			class="flex items-center gap-2 px-3 py-2 bg-gray-900/80 border-b border-white/10 cursor-move select-none"
			role="toolbar"
			tabindex={0}
			onmousedown={startDragPanel}
		>
			<!-- Status dot -->
			<span
				class="w-2 h-2 rounded-full flex-shrink-0 {isBddLocal
					? 'bg-yellow-400'
					: 'bg-red-500'} animate-pulse"
			></span>
			<span class="text-xs font-semibold {isBddLocal ? 'text-yellow-400' : 'text-red-400'}">
				{isBddLocal ? 'BDD locale' : 'BDD prod'}
			</span>

			<div class="flex-1"></div>

			<!-- Tabs -->
			<div class="flex bg-gray-800/60 rounded-lg p-0.5 gap-0.5">
				{#each [{ id: 'env', label: 'Env' }, { id: 'page', label: 'Page' }, { id: 'logs', label: 'Logs' }] as tab (tab.id)}
					<button
						onclick={() => (activeTab = tab.id as 'env' | 'page' | 'logs')}
						class="px-2.5 py-1 rounded-md text-xs font-medium transition-all
							{activeTab === tab.id ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}"
					>
						{tab.label}
					</button>
				{/each}
			</div>

			<button
				onclick={() => (open = false)}
				class="ml-1 p-1 rounded-md text-gray-500 hover:text-gray-200 hover:bg-white/10 transition-colors"
				aria-label="Fermer"
			>
				<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			</button>
		</div>

		<!-- Content -->
		<div class="flex-1 overflow-hidden flex flex-col min-h-0">
			<!-- ENV TAB -->
			{#if activeTab === 'env'}
				<div class="flex-1 overflow-y-auto p-2 space-y-0.5">
					{#if envPriv}
						{#each Object.entries(envPriv) as [key, value] (key)}
							<button
								class="w-full text-left flex gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 group transition-colors"
								onclick={() => copyEnvValue(value)}
								title="Cliquer pour copier"
							>
								<span
									class="text-xs font-mono font-semibold text-yellow-400/90 flex-shrink-0 w-44 truncate"
									>{key}</span
								>
								<span class="text-xs font-mono text-blue-300/80 break-all line-clamp-2 flex-1"
									>{value}</span
								>
								<svg
									class="w-3 h-3 text-gray-600 group-hover:text-gray-400 flex-shrink-0 mt-0.5 transition-colors"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
									/>
								</svg>
							</button>
						{/each}
					{/if}
					{#if import.meta.env}
						<p class="px-2 pt-3 pb-1 text-xs text-gray-500 font-semibold uppercase tracking-wider">
							Vite env
						</p>
						{#each Object.entries(import.meta.env) as [key, value] (key)}
							<button
								class="w-full text-left flex gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 group transition-colors"
								onclick={() => copyEnvValue(value)}
								title="Cliquer pour copier"
							>
								<span
									class="text-xs font-mono font-semibold text-yellow-400/90 flex-shrink-0 w-44 truncate"
									>{key}</span
								>
								<span class="text-xs font-mono text-blue-300/80 break-all line-clamp-2 flex-1"
									>{value}</span
								>
								<svg
									class="w-3 h-3 text-gray-600 group-hover:text-gray-400 flex-shrink-0 mt-0.5 transition-colors"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
									/>
								</svg>
							</button>
						{/each}
					{/if}
				</div>

				<!-- PAGE TAB -->
			{:else if activeTab === 'page'}
				<div class="flex-1 overflow-y-auto p-3 bg-gray-950/50">
					<JsonTree value={page.data as never} depth={0} />
				</div>

				<!-- LOGS TAB -->
			{:else if activeTab === 'logs'}
				<!-- Logs toolbar -->
				<div class="flex items-center gap-2 px-2 py-1.5 border-b border-white/10 bg-gray-900/40">
					<select
						bind:value={logFile}
						onchange={fetchLogs}
						class="text-xs bg-gray-800 border border-white/10 rounded px-1.5 py-0.5 text-gray-300 focus:outline-none"
					>
						<option value="combined">combined</option>
						<option value="error">error</option>
					</select>

					<select
						bind:value={logLevel}
						class="text-xs bg-gray-800 border border-white/10 rounded px-1.5 py-0.5 text-gray-300 focus:outline-none"
					>
						<option value="all">All levels</option>
						<option value="info">info</option>
						<option value="warn">warn</option>
						<option value="error">error</option>
						<option value="debug">debug</option>
					</select>

					<input
						bind:value={logSearch}
						placeholder="Filtrer…"
						class="flex-1 text-xs bg-gray-800 border border-white/10 rounded px-2 py-0.5 text-gray-300 placeholder-gray-600 focus:outline-none focus:border-gray-600"
					/>

					<button
						onclick={fetchLogs}
						class="p-1 text-gray-500 hover:text-gray-200 transition-colors"
						title="Rafraîchir"
					>
						<svg
							class="w-3.5 h-3.5 {logsLoading ? 'animate-spin' : ''}"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							/>
						</svg>
					</button>

					<button
						onclick={() => (liveLog = !liveLog)}
						class="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium transition-colors
							{liveLog ? 'text-emerald-400 bg-emerald-500/10' : 'text-gray-500 hover:text-gray-300'}"
						title="Live tail"
					>
						<span
							class="w-1.5 h-1.5 rounded-full {liveLog
								? 'bg-emerald-400 animate-pulse'
								: 'bg-gray-600'}"
						></span>
						Live
					</button>
				</div>

				<!-- Log entries -->
				<div class="flex-1 overflow-y-auto font-mono text-xs">
					{#if filteredLogs.length === 0}
						<p class="text-center text-gray-600 py-8">
							{logsLoading ? 'Chargement…' : 'Aucun log'}
						</p>
					{:else}
						{#each filteredLogs as entry, i (i)}
							<div
								class="flex gap-2 px-2 py-1 hover:bg-white/5 transition-colors border-b border-white/[0.03] {levelBg(
									String(entry.level ?? '')
								)}"
							>
								<span class="flex-shrink-0 text-gray-600 w-16"
									>{formatTime(String(entry.timestamp ?? ''))}</span
								>
								<span
									class="flex-shrink-0 w-10 {levelColor(
										String(entry.level ?? 'info')
									)} font-semibold uppercase">{entry.level}</span
								>
								<span class="text-gray-300 break-all leading-relaxed">
									{#if entry.route}
										<span class="text-blue-400">[{entry.method} {entry.route}]</span>{' '}
									{/if}
									{entry.message}
									{#if entry.duration}
										<span class="text-gray-500 ml-1">{entry.duration}ms</span>
									{/if}
								</span>
							</div>
						{/each}
					{/if}
				</div>

				<!-- Log count -->
				<div
					class="px-3 py-1 border-t border-white/10 bg-gray-900/40 flex items-center justify-between"
				>
					<span class="text-xs text-gray-600">{filteredLogs.length} entrées</span>
					<label class="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
						<input type="checkbox" bind:checked={autoScroll} class="w-3 h-3 accent-emerald-500" />
						Auto-scroll
					</label>
				</div>
			{/if}
		</div>
	</div>
{/if}
